/** Small numeric helpers shared by the analysis code. Pure, no allocation on hot paths. */

export function rms(samples: Float32Array, from = 0, to = samples.length): number {
  let acc = 0;
  for (let i = from; i < to; i++) acc += samples[i] * samples[i];
  const n = Math.max(1, to - from);
  return Math.sqrt(acc / n);
}

export function amplitudeToDb(amplitude: number, floor = 1e-9): number {
  return 20 * Math.log10(Math.max(amplitude, floor));
}

/** Median of a copy — safe for small windows, which is all we use it on. */
export function median(values: ArrayLike<number>): number {
  const n = values.length;
  if (n === 0) return 0;
  const sorted = Array.from(values as ArrayLike<number>).sort((a, b) => a - b);
  const mid = n >> 1;
  return n % 2 === 0 ? (sorted[mid - 1] + sorted[mid]) / 2 : sorted[mid];
}

/**
 * Median absolute deviation — a robust spread estimate.
 *
 * Used instead of the standard deviation wherever the sample deliberately
 * contains the outliers we are looking for (onset spikes in a flux history):
 * a standard deviation would be inflated by its own targets.
 */
export function mad(values: ArrayLike<number>, centre = median(values)): number {
  const n = values.length;
  if (n === 0) return 0;
  const deviations = new Array<number>(n);
  for (let i = 0; i < n; i++) deviations[i] = Math.abs(values[i] - centre);
  return median(deviations);
}

export function mean(values: ArrayLike<number>): number {
  if (values.length === 0) return 0;
  let acc = 0;
  for (let i = 0; i < values.length; i++) acc += values[i];
  return acc / values.length;
}

export function stdDev(values: ArrayLike<number>): number {
  if (values.length < 2) return 0;
  const m = mean(values);
  let acc = 0;
  for (let i = 0; i < values.length; i++) {
    const d = values[i] - m;
    acc += d * d;
  }
  return Math.sqrt(acc / (values.length - 1));
}

export function clamp(value: number, min: number, max: number): number {
  return value < min ? min : value > max ? max : value;
}

export function clamp01(value: number): number {
  return clamp(value, 0, 1);
}

/** Maps `value` from [lo, hi] onto [0, 1], clamped. */
export function normalizeRange(value: number, lo: number, hi: number): number {
  if (hi === lo) return value >= hi ? 1 : 0;
  return clamp01((value - lo) / (hi - lo));
}

/**
 * Spectral flatness (Wiener entropy): geometric mean over arithmetic mean.
 * ~1 for noise, near 0 for a strongly pitched signal.
 */
export function spectralFlatness(magnitudes: Float32Array, from = 0, to = magnitudes.length): number {
  let logSum = 0;
  let sum = 0;
  let count = 0;
  const epsilon = 1e-10;
  for (let i = from; i < to; i++) {
    const m = magnitudes[i] + epsilon;
    logSum += Math.log(m);
    sum += m;
    count++;
  }
  if (count === 0 || sum === 0) return 1;
  const geometric = Math.exp(logSum / count);
  const arithmetic = sum / count;
  return clamp01(geometric / arithmetic);
}

/** Normalises in place so the maximum becomes 1. Leaves an all-zero array alone. */
export function normalizeMax(values: Float32Array): Float32Array {
  let max = 0;
  for (let i = 0; i < values.length; i++) {
    if (values[i] > max) max = values[i];
  }
  if (max <= 0) return values;
  for (let i = 0; i < values.length; i++) values[i] /= max;
  return values;
}

